import { api } from '../../shared/api/client'
import { MARKETPLACE_CODES, type MarketplaceCode } from '../../shared/marketplaces'

/**
 * Площадка значений характеристик категории (`base_category_mp_characteristics`).
 *
 * НЕ переиспользует `MpMarketplace` из `features/marketplace-mapping/api` —
 * тот тип принадлежит другой сущности (схема сопоставления
 * `mp_characteristic_mappings`, у неё для Я.Маркет пока нет данных, см.
 * SPEC_2026-09-13_YANDEX_MARKET.md, этап 2) и намеренно сужен до `wb | ozon`.
 * Значения категории и схема сопоставления совпадали по набору площадок
 * случайно — здесь берём полный список из реестра площадок.
 */
export type CategoryMpMarketplace = MarketplaceCode

/** Площадки, у категории которых ПИМ хранит собственные значения характеристик. */
export const CATEGORY_MP_CHARACTERISTIC_MARKETPLACES = MARKETPLACE_CODES

export interface CategoryMpCharacteristicValue {
  id?: number
  marketplace: CategoryMpMarketplace
  scope_ref: string
  target_id: number
  target_name: string
  value: string
  value_id: number | string | null
  sort_order: number
  updated_at?: string
}

export interface CategoryMpCharacteristicsResponse {
  category_id: number
  prefix: string
  current_scope_ref: Partial<Record<CategoryMpMarketplace, string>>
  values: CategoryMpCharacteristicValue[]
  saved?: number[]
  removed?: number[]
}

export interface CategoryMpCharacteristicDraft {
  values: Record<string, CategoryMpCharacteristicValue>
  originalValues: Record<string, CategoryMpCharacteristicValue>
  dirtyTargetIds: number[]
  loadedCategoryId: number | null
}

export type CategoryMpCharacteristicsDrafts = Record<
  CategoryMpMarketplace,
  CategoryMpCharacteristicDraft
>

export function emptyMpCharacteristicDraft(): CategoryMpCharacteristicDraft {
  return {
    values: {},
    originalValues: {},
    dirtyTargetIds: [],
    loadedCategoryId: null,
  }
}

export function emptyMpCharacteristicsDrafts(): CategoryMpCharacteristicsDrafts {
  return Object.fromEntries(
    CATEGORY_MP_CHARACTERISTIC_MARKETPLACES.map((mp) => [mp, emptyMpCharacteristicDraft()]),
  ) as CategoryMpCharacteristicsDrafts
}

function unwrap<T>(response: any): T {
  return (response?.data?.data ?? response?.data) as T
}

export const categoryMpCharacteristicsAPI = {
  get: async (
    categoryId: number,
    marketplace?: CategoryMpMarketplace,
  ): Promise<CategoryMpCharacteristicsResponse> =>
    unwrap<CategoryMpCharacteristicsResponse>(
      await api.get(`/categories/${categoryId}/mp-characteristics`, {
        params: marketplace ? { marketplace } : undefined,
      }),
    ),

  save: async (
    categoryId: number,
    marketplace: CategoryMpMarketplace,
    values: CategoryMpCharacteristicValue[],
  ): Promise<CategoryMpCharacteristicsResponse> =>
    unwrap<CategoryMpCharacteristicsResponse>(
      await api.put(`/categories/${categoryId}/mp-characteristics`, {
        marketplace,
        values: values.map(({ target_id, target_name, value, value_id, sort_order }) => ({
          target_id,
          target_name,
          value,
          value_id,
          sort_order,
        })),
      }),
  ),
}

export type MpCharacteristicDraftSaved = (
  marketplace: CategoryMpMarketplace,
  draft: CategoryMpCharacteristicDraft,
) => void

/**
 * Точечно сохраняет dirty-площадки в стабильном порядке из реестра (ВБ → ОЗОН
 * → Я.Маркет). После успешной площадки callback вызывается сразу: если
 * следующая упадёт, повторный запуск не отправит уже завершённую площадку ещё
 * раз.
 */
export async function saveDirtyMpCharacteristics(
  categoryId: number,
  drafts: CategoryMpCharacteristicsDrafts,
  onSaved?: MpCharacteristicDraftSaved,
): Promise<void> {
  for (const marketplace of CATEGORY_MP_CHARACTERISTIC_MARKETPLACES) {
    const draft = drafts[marketplace]
    if (draft.dirtyTargetIds.length === 0) continue
    await categoryMpCharacteristicsAPI.save(
      categoryId,
      marketplace,
      draft.dirtyTargetIds
        .map((targetId) => draft.values[String(targetId)])
        .filter(Boolean),
    )
    onSaved?.(marketplace, {
      ...draft,
      originalValues: { ...draft.values },
      dirtyTargetIds: [],
      loadedCategoryId: categoryId,
    })
  }
}

/** ID созданной категории из axios/gateway response. */
export function categoryIdFromCreateResponse(response: any): number | null {
  const payload = response?.data?.data ?? response?.data ?? response
  const categoryId = Number(payload?.category_id ?? payload?.id)
  return Number.isInteger(categoryId) && categoryId > 0 ? categoryId : null
}
